import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { IconBoardMockup } from "@/components/IconBoardMockup";
import { Check } from "lucide-react";

const POINTS = [
  {
    title: "Bigger targets, faster taps.",
    body: "Large square tap zones beat narrow text lines, cutting movement time dramatically.",
  },
  {
    title: "Less to think about.",
    body: "Distinct colours and shapes let the eye filter instantly — no line-by-line scanning of a scrolling list.",
  },
  {
    title: "Muscle memory kicks in.",
    body: "Fixed positions mean supervisors log the right error in the right place — every time.",
  },
];

export default function IconBoard() {
  return (
    <section
      id="icon-board"
      data-testid="iconboard-section"
      className="relative grain bg-[#2B2B2B] text-white py-20 lg:py-28 overflow-hidden"
    >
      <div className="absolute inset-0 techgrid-dark opacity-60" />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#2BBFB3]/10 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center">
        {/* phone spotlight */}
        <div className="lg:col-span-5 flex justify-center lg:justify-start">
          <Reveal>
            <motion.div
              initial={{ opacity: 0, y: 30, rotate: -2 }}
              whileInView={{ opacity: 1, y: 0, rotate: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="relative">
                <div className="absolute inset-0 -m-10 rounded-full bg-[#2BBFB3]/12 blur-3xl" />
                <div className="relative">
                  <IconBoardMockup />
                </div>
              </div>
            </motion.div>
          </Reveal>
        </div>

        {/* copy */}
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal>
            <p className="overline text-[#2BBFB3]">Tap. Don't type.</p>
            <h2 className="mt-5 font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.02]">
              An icon board built for split-second logging.
            </h2>
            <p className="mt-6 max-w-lg text-lg text-white/65 leading-relaxed">
              A grid of large, colour-coded icons is faster and more accurate than scrolling a menu of words.
            </p>
          </Reveal>

          <div className="mt-9 space-y-5">
            {POINTS.map((p, i) => (
              <Reveal key={p.title} delay={0.1 + i * 0.08}>
                <div className="flex gap-3.5">
                  <span className="grid place-items-center w-6 h-6 mt-0.5 rounded-full bg-[#2BBFB3] text-[#0f1211] shrink-0">
                    <Check size={14} strokeWidth={3} />
                  </span>
                  <p className="text-white/70 leading-relaxed">
                    <span className="font-semibold text-white">{p.title}</span> {p.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
